import { useEffect } from 'react';

export interface PageSEOOptions {
  title: string;
  description: string;
  urlPath: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  geoPosition?: string;
  geoPlacename?: string;
  geoRegion?: string;
  jsonLdSchema?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  nofollow?: boolean;
}

export function usePageSEO(options: PageSEOOptions) {
  const {
    title,
    description,
    urlPath,
    keywords,
    ogTitle,
    ogDescription,
    ogImage = '/og/og-default.jpg',
    ogImageAlt = 'Couvreur à Lévis - Soumission gratuite en 2h',
    ogType = 'website',
    geoPosition,
    geoPlacename,
    geoRegion,
    jsonLdSchema,
    noindex,
    nofollow,
  } = options;

  useEffect(() => {
    // Nettoyer les balises managées dynamiquement avant réinjection
    document.querySelectorAll('[data-seo-managed="true"]').forEach((el) => el.remove());

    document.title = title;

    function setMetaName(name: string, content: string) {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    }

    function setMetaProperty(property: string, content: string) {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    }

    function setMetaHttpEquiv(httpEquiv: string, content: string) {
      let el = document.querySelector(`meta[http-equiv="${httpEquiv}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('http-equiv', httpEquiv);
        el.setAttribute('data-seo-managed', 'true');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    }

    function setCanonical(href: string) {
      let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    }

    function setHreflang(hreflang: string, href: string) {
      let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        el.setAttribute('data-seo-managed', 'true');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    }

    function setJsonLd(schemas: Record<string, unknown> | Record<string, unknown>[]) {
      // Remove existing JSON-LD scripts managed by this hook
      const existing = document.querySelectorAll('script[data-seo-jsonld="true"]');
      existing.forEach((script) => script.remove());

      const schemaArray = Array.isArray(schemas) ? schemas : [schemas];
      schemaArray.forEach((schema) => {
        if (!schema.inLanguage) {
          (schema as Record<string, unknown>).inLanguage = 'fr-CA';
        }
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo-jsonld', 'true');
        script.textContent = JSON.stringify(schema, null, 2);
        document.head.appendChild(script);
      });
    }

    // Standard meta
    setMetaName('description', description);
    if (keywords) setMetaName('keywords', keywords);
    if (geoPosition) setMetaName('geo.position', geoPosition);
    if (geoPlacename) setMetaName('geo.placename', geoPlacename);
    if (geoRegion) setMetaName('geo.region', geoRegion);

    // Meta geo additionnelles — signaux géographiques Québécois
    setMetaName('ICBM', '46.7903, -71.1844');
    setMetaName('DC.coverage', 'Lévis, Québec, Canada');
    setMetaName('DC.language', 'fr-CA');
    setMetaName('language', 'French');
    setMetaHttpEquiv('content-language', 'fr-CA');

    // Technical meta
    if (noindex) {
      const robotsValue = nofollow === false ? 'noindex, follow' : 'noindex, nofollow';
      setMetaName('robots', robotsValue);
      setMetaName('googlebot', robotsValue);
    } else {
      setMetaName('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
      setMetaName('googlebot', 'index, follow');
    }
    setMetaName('format-detection', 'telephone=yes');
    setMetaName('theme-color', '#D97706');

    // Canonical
    setCanonical(`https://couvreur-levis.com${urlPath}`);

    // Hreflang — indique la version canadienne-française
    setHreflang('fr-CA', `https://couvreur-levis.com${urlPath}`);
    setHreflang('x-default', `https://couvreur-levis.com${urlPath}`);

    // Open Graph — utilise ogTitle/ogDescription si fournis, sinon fallback sur title/description
    setMetaProperty('og:title', ogTitle || title);
    setMetaProperty('og:description', ogDescription || description);
    setMetaProperty('og:url', `https://couvreur-levis.com${urlPath}`);
    setMetaProperty('og:type', ogType);
    setMetaProperty('og:locale', 'fr_CA');
    setMetaProperty('og:site_name', 'Couvreur-Lévis.com');
    setMetaProperty('og:image', ogImage);
    setMetaProperty('og:image:width', '1200');
    setMetaProperty('og:image:height', '630');
    setMetaProperty('og:image:alt', ogImageAlt);
    setMetaProperty('og:image:type', 'image/jpeg');

    // Twitter Card
    setMetaName('twitter:card', 'summary_large_image');
    setMetaName('twitter:title', ogTitle || title);
    setMetaName('twitter:description', ogDescription || description);
    setMetaName('twitter:image', ogImage);
    setMetaName('twitter:image:alt', ogImageAlt);
    setMetaName('twitter:site', '@couvreurlevis');

    // JSON-LD Schema.org
    if (jsonLdSchema) {
      setJsonLd(jsonLdSchema);
    }

    window.scrollTo(0, 0);

    return () => {
      document.querySelectorAll('[data-seo-managed="true"]').forEach((el) => el.remove());
    };
  }, [title, description, urlPath, keywords, ogTitle, ogDescription, ogImage, ogImageAlt, ogType, geoPosition, geoPlacename, geoRegion, jsonLdSchema, noindex, nofollow]);
}